import { Dialog } from '@headlessui/react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import * as scenarioApi from 'api/scenarios';
import { Alert, Button, Input, Modal } from 'components';
import { ConfirmModal } from 'components/ConfirmModal';
import { Pill } from 'components/ManagementCard';
import { ManagementFilterBar } from 'components/ManagementFilters';
import { Snackbar } from 'components/Snackbar';
import { Textarea } from 'components/Textarea';
import { pageCardClasses } from 'components/AppLayout/styles';
import { routes, stripOrganizationScopePrefix } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import { useLineChannelAccounts } from 'hooks';
import { ELineChannelStatus } from 'types';
import { apiIssueCode, issueMessage } from 'utils/scenarioIssues';

import { copyName, reissueDefinitionIds } from './copy';
import { stateLabels, stateTones, triggerLabels } from './labels';
import { accountText, ALL_ACCOUNTS_LABEL, resolveTargets, summarizeTargets } from './targets';

const states = Object.keys(stateLabels) as scenarioApi.ScenarioState[];
const date = new Intl.DateTimeFormat('ja-JP', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Tokyo',
}).format;
const selectClass = 'h-11 w-full rounded-2xl border border-border/80 bg-white px-3 text-sm';
const labelClass = 'grid gap-2 text-xs font-semibold text-ink';
const queryKey = 'scenarios';

function path(template: string, id: number) {
  return template.replace(':scenarioId', String(id));
}

/**
 * 選択後に対象アカウントが消えた／別組織のIDが混ざった場合、serverはcodeなしの404を返す。
 * そのままだと通信ライブラリの英文が出てしまうため、選び直しへ誘導する。
 */
// 作成は常に全アカウント対象なのでアカウント個別の404／409は起きない
function createErrorMessage(error: unknown) {
  return issueMessage(error, 'シナリオを作成できませんでした。');
}

function Meta({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted">{label}</dt>
      <dd className="mt-1 flex min-w-0 items-baseline gap-1 text-sm text-ink" title={title}>
        {children}
      </dd>
    </div>
  );
}

function ScenarioCard({ scenario, onCopy, copying, onArchive, onRestore, archiving }: { scenario: scenarioApi.ScenarioListItem; onCopy: () => void; copying: boolean; onArchive: () => void; onRestore: () => void; archiving: boolean }) {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const edit = orgPathFor(path(routes.SCENARIO_EDIT, scenario.id));
  const stats = orgPathFor(path(routes.SCENARIO_STATS, scenario.id));
  const target = summarizeTargets(resolveTargets(scenario));
  const archived = scenario.state === 'archived';

  // prettier-ignore
  return (
    <article className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm transition hover:shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid min-w-0 gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="min-w-0 break-words text-lg font-semibold text-ink">{scenario.name}</h2>
            <Pill tone={stateTones[scenario.state]}>{stateLabels[scenario.state]}</Pill>
            {scenario.hasUnpublishedChanges && <Pill tone="accent">未公開の変更あり</Pill>}
          </div>
          <p className="text-xs text-muted">最終更新 {date(new Date(scenario.updatedAt))}</p>
        </div>
        {/* アーカイブは進行中の配信を取り消す破壊的操作なので、確認ダイアログを挟む（復元しても配信は戻らない） */}
        <div data-testid="scenario-card-actions" className="flex flex-wrap gap-2 sm:shrink-0">{archived ? <Button size="sm" variant="outline" loading={archiving} onClick={onRestore}>アーカイブから戻す</Button> : <><Button size="sm" variant="outline" loading={copying} onClick={onCopy}>コピー</Button><Button size="sm" variant="outline" loading={archiving} onClick={onArchive}>アーカイブ</Button></>}<Button size="sm" variant="outline" onClick={() => navigate(stats)}>統計</Button><Button size="sm" onClick={() => navigate(edit)}>編集</Button></div>
      </div>
      {scenario.resumableFailedRunCount > 0 && (
        <button type="button" className="flex w-full items-center justify-between gap-3 rounded-2xl border border-danger-border bg-danger-soft px-4 py-3 text-left text-sm font-semibold text-danger transition hover:bg-danger-soft/60" onClick={() => navigate(`${stats}?view=failures`)}>
          <span>配信失敗 {scenario.resumableFailedRunCount}件（再開できます）</span>
          <span className="shrink-0 text-xs font-semibold">失敗一覧を見る →</span>
        </button>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Meta label="対象アカウント" title={target.title}>
          <span className="min-w-0 truncate">{target.lead}</span>
          {target.more > 0 && <span className="shrink-0 text-xs text-muted">他{target.more}件</span>}
        </Meta>
        <Meta label="開始条件">{scenario.publishedTrigger ? triggerLabels[scenario.publishedTrigger] : '未公開'}</Meta>
        <Meta label="ステップ / 公開版">{`${scenario.stepCount} / ${scenario.currentPublishNumber ? `第${scenario.currentPublishNumber}版` : '未公開'}`}</Meta>
      </dl>
    </article>
  );
}

type CreateProps = {
  onClose: () => void;
};

function CreateScenarioDialog({ onClose }: CreateProps) {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string>();
  const mutation = useMutation({
    mutationFn: scenarioApi.createScenario,
    onSuccess: (scenario) => {
      client.invalidateQueries({ queryKey: [queryKey] });
      navigate(orgPathFor(path(routes.SCENARIO_EDIT, scenario.id)), { replace: true });
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const cleanName = name.trim();
    const cleanDescription = description.trim();
    if (!cleanName) return setValidationError('シナリオ名を入力してください。');
    if (cleanName.length > 100 || cleanDescription.length > 500) return setValidationError('入力文字数を確認してください。');
    setValidationError(undefined);
    // 常に組織の全LINE公式アカウントを対象にする（lineChannelIds自体を送らない）
    mutation.mutate({
      name: cleanName,
      ...(cleanDescription ? { description: cleanDescription } : {}),
    });
  };
  const error = validationError || (mutation.error ? createErrorMessage(mutation.error) : undefined);

  // prettier-ignore
  return (
    <Modal open onClose={mutation.isPending ? () => undefined : onClose}>
      <Dialog.Title className="text-xl font-semibold text-ink">ステップ配信を作成</Dialog.Title>
      <form className="mt-5 grid gap-4" onSubmit={submit}>
        {error && <Alert>{error}</Alert>}
        <div className="grid gap-1 rounded-2xl bg-neutral-25 p-3 text-xs text-ink">
          <p className="font-semibold">対象アカウント：{ALL_ACCOUNTS_LABEL}</p>
          <p className="font-normal text-muted">あとから追加したLINE公式アカウントも自動で対象になります。</p>
        </div>
        <label className={labelClass}>シナリオ名<Input value={name} maxLength={100} disabled={mutation.isPending} onChange={(event) => setName(event.target.value)} /></label>
        <label className={labelClass}>説明（任意）<Textarea value={description} maxLength={500} disabled={mutation.isPending} onChange={(event) => setDescription(event.target.value)} /></label>
        <div className="mt-2 flex justify-end gap-2"><Button type="button" variant="outline" disabled={mutation.isPending} onClick={onClose}>キャンセル</Button><Button type="submit" loading={mutation.isPending}>作成して編集へ</Button></div>
      </form>
    </Modal>
  );
}

function Message({ children }: { children: string }) {
  return <p className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted">{children}</p>;
}

function EmptyState({ filtered, onCreate }: { filtered: boolean; onCreate: () => void }) {
  // prettier-ignore
  return (
    <div className="grid justify-items-center gap-3 rounded-2xl border border-dashed border-border bg-white/60 px-6 py-12 text-center">
      <p className="text-sm font-semibold text-ink">{filtered ? '条件に合うステップ配信はありません' : 'ステップ配信はまだありません'}</p>
      <p className="max-w-md text-xs leading-relaxed text-muted">
        {filtered
          ? 'アカウントや状態の絞り込みを変えると、ほかのシナリオが見つかることがあります。'
          : '友だち追加やタグ付与をきっかけに、決めた順番とタイミングでメッセージを自動でお送りできます。'}
      </p>
      {!filtered && <Button size="sm" onClick={onCreate}>最初のステップ配信を作る</Button>}
    </div>
  );
}

export default function Scenarios() {
  const location = useLocation();
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [cursorNotice, setCursorNotice] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [copySuccess, setCopySuccess] = useState<string>();
  const [copyingId, setCopyingId] = useState<number | null>(null);
  // アーカイブ確認中のシナリオ。復元は取り消しても配信が戻らないだけなので確認を挟まない
  const [confirmArchive, setConfirmArchive] = useState<scenarioApi.ScenarioListItem | null>(null);
  const [stateTargetId, setStateTargetId] = useState<number | null>(null);
  const { accounts, loading: accountsLoading, error: accountsError } = useLineChannelAccounts();
  const accountId = params.get('lineChannelId') ?? '';
  const requestedAccountId = /^[1-9]\d*$/.test(accountId) && Number.isSafeInteger(Number(accountId)) ? Number(accountId) : null;
  const stateValue = params.get('state');
  const state = states.includes(stateValue as scenarioApi.ScenarioState) ? (stateValue as scenarioApi.ScenarioState) : '';
  const scenarios = useInfiniteQuery({
    // filterやstateを変えるとqueryKeyが変わり、古いcursorを引き継がず先頭ページから取得する
    queryKey: [queryKey, requestedAccountId, state || null],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      scenarioApi.listScenarios({
        ...(requestedAccountId === null ? {} : { lineChannelId: requestedAccountId }),
        ...(state ? { state } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
  });
  const items = scenarios.data?.pages.flatMap((page) => page.items) ?? [];
  const listedAccount = scenarios.data?.pages[0]?.lineChannel;
  const accountWarning = listedAccount?.status === ELineChannelStatus.PAUSED
    ? 'LINE公式アカウント停止中です。シナリオが「配信中」でも、アカウント復旧まで配信されません。'
    : listedAccount?.status === ELineChannelStatus.UNLINKED ? 'LINE公式アカウント連携解除済みです。シナリオの状態にかかわらず配信されません。' : undefined;
  const detachedAccount = listedAccount && !accounts.some(({ id }) => id === listedAccount.id) ? listedAccount : null;
  const listError = scenarios.error ? issueMessage(scenarios.error, 'シナリオを取得できませんでした。') : undefined;
  const invalidCursor = apiIssueCode(scenarios.error) === 'INVALID_CURSOR';
  useEffect(() => {
    if (!invalidCursor) return;
    setCursorNotice('一覧の読み込み位置が古くなったため、先頭から読み込み直しました。');
    void client.resetQueries({ queryKey: [queryKey, requestedAccountId, state || null] });
  }, [invalidCursor, client, requestedAccountId, state]);
  const updateParam = (key: 'lineChannelId' | 'state', value: string) => {
    const updated = new URLSearchParams(params);
    value ? updated.set(key, value) : updated.delete(key);
    setCursorNotice(undefined);
    setParams(updated);
  };
  // 内容だけを引き継いだ下書きを作る。作成APIはdefinitionを受け取らないので、
  // 作成してから編集APIで流し込む。途中で失敗した場合は空の下書きが残る
  const copy = useMutation({
    mutationFn: async (scenario: scenarioApi.ScenarioListItem) => {
      const { detail } = await scenarioApi.getScenario(scenario.id);
      const created = await scenarioApi.createScenario({
        name: copyName(scenario.name),
        ...(detail.description ? { description: detail.description } : {}),
        ...(scenario.targetsAllAccounts ? {} : { lineChannelIds: scenario.lineChannels.map(({ id }) => id) }),
      });
      return scenarioApi.updateScenario(created.id, {
        expectedEditVersion: created.editVersion,
        definition: reissueDefinitionIds(detail.definition),
      });
    },
    onSuccess: (created) => {
      setCopyingId(null);
      // 成功はエラー用Alertでなく成功Snackbarで出す（他エラーがあると通知が隠れるため）
      setCopySuccess(`「${created.name}」を下書きとして作成しました。`);
      void client.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (error: Error) => {
      setCopyingId(null);
      setNotice(issueMessage(error, 'シナリオをコピーできませんでした。'));
    },
  });
  // アーカイブ／復元は一覧の絞り込み結果そのものが変わるため、成功時は一覧を取り直す
  const changeState = useMutation({
    mutationFn: ({ scenario, action }: { scenario: scenarioApi.ScenarioListItem; action: 'archive' | 'restore' }) =>
      scenarioApi.changeScenarioState(scenario.id, { action, expectedState: scenario.state }),
    onSuccess: (_record, { action }) => {
      setStateTargetId(null);
      setConfirmArchive(null);
      setCopySuccess(action === 'archive' ? 'アーカイブしました。' : 'アーカイブから戻しました。');
      void client.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (error: Error) => {
      setStateTargetId(null);
      // 確認モーダルを閉じてから通知しないと、エラーAlertがオーバーレイの背後に隠れて見えない
      setConfirmArchive(null);
      setNotice(issueMessage(error, '状態を更新できませんでした。'));
      // 失敗（特にexpectedState不一致の409）は手元の一覧が古いサイン。
      // 取り直して、次の操作が最新のstateで飛ぶようにする
      void client.invalidateQueries({ queryKey: [queryKey] });
    },
  });
  const route = (pathname: string) => navigate({ pathname: orgPathFor(pathname), search: params.toString() });

  // prettier-ignore
  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div><h1 className="text-2xl font-display text-ink">ステップ配信</h1><p className="mt-2 text-sm text-muted">組織のシナリオを管理します。配信先は組織のLINE公式アカウント全体で、あとから追加したアカウントも自動で対象になります。</p></div>
          <Button size="sm" onClick={() => route(routes.SCENARIOS_NEW)}>新規作成</Button>
        </header>
        {(accountsError || listError || cursorNotice || notice) && <div className="mt-6"><Alert>{accountsError || listError || cursorNotice || notice}</Alert></div>}
        {accountWarning && <div className="mt-6"><Alert>{accountWarning}</Alert></div>}
        <ManagementFilterBar>
          <label className="grid min-w-[240px] flex-1 gap-2 text-xs font-semibold text-ink">LINE公式アカウント
            <select className={`${selectClass} bg-white/80 text-ink`} value={requestedAccountId ?? ''} disabled={accountsLoading} onChange={(event) => updateParam('lineChannelId', event.target.value)}>
              <option value="">すべてのアカウント</option>{detachedAccount && <option value={detachedAccount.id}>{accountText(detachedAccount)}（連携解除済み）</option>}{accounts.map((account) => <option key={account.id} value={account.id}>{accountText(account)}</option>)}
            </select>
          </label>
          <label className="grid min-w-[180px] gap-2 text-xs font-semibold text-ink">状態
            <select className={`${selectClass} bg-white/80 text-ink`} value={state} onChange={(event) => updateParam('state', event.target.value)}>
              <option value="">アーカイブ以外</option>{states.map((value) => <option key={value} value={value}>{stateLabels[value]}</option>)}
            </select>
          </label>
        </ManagementFilterBar>
        <div className="mt-6 grid gap-4">
          {scenarios.isPending ? <Message>読み込み中...</Message>
            : items.length ? items.map((scenario) => <ScenarioCard key={scenario.id} scenario={scenario} copying={copy.isPending && copyingId === scenario.id} onCopy={() => { setNotice(undefined); setCopyingId(scenario.id); copy.mutate(scenario); }} archiving={changeState.isPending && stateTargetId === scenario.id} onArchive={() => { setNotice(undefined); setConfirmArchive(scenario); }} onRestore={() => { setNotice(undefined); setStateTargetId(scenario.id); changeState.mutate({ scenario, action: 'restore' }); }} />)
              : <EmptyState filtered={requestedAccountId !== null || Boolean(state)} onCreate={() => route(routes.SCENARIOS_NEW)} />}
        </div>
        {scenarios.hasNextPage && <div className="mt-6 flex justify-center"><Button variant="outline" loading={scenarios.isFetchingNextPage} onClick={() => scenarios.fetchNextPage()}>さらに読み込む</Button></div>}
      </section>
      {stripOrganizationScopePrefix(location.pathname) === routes.SCENARIOS_NEW && <CreateScenarioDialog onClose={() => route(routes.SCENARIOS)} />}
      <ConfirmModal
        open={Boolean(confirmArchive)}
        textTitle="シナリオをアーカイブしますか？"
        textMessage={`「${confirmArchive?.name ?? ''}」の進行中の配信はすべて取り消され、一覧から隠れます。公開した内容と配信の記録は残りますが、取り消した配信は復元しても元に戻りません。`}
        textCancel="やめる"
        textConfirm="アーカイブする"
        confirmButtonClassName="border-danger bg-danger text-white !shadow-none hover:border-danger hover:bg-danger hover:text-white"
        isLoading={changeState.isPending}
        onClose={() => setConfirmArchive(null)}
        onConfirm={() => { if (confirmArchive) { setStateTargetId(confirmArchive.id); changeState.mutate({ scenario: confirmArchive, action: 'archive' }); } }}
      />
      <Snackbar open={Boolean(copySuccess)} message={copySuccess} variant="success" onClose={() => setCopySuccess(undefined)} autoHideDuration={3000} />
    </>
  );
}
